import {
	ChartContainer,
	ChartTooltip,
	ChartTooltipContent,
} from "@/components/ui/chart";
import { cn } from "@/lib/utils";
import dayjs from "dayjs";
import {
	CartesianGrid,
	Line,
	LineChart,
	ResponsiveContainer,
	XAxis,
	YAxis,
} from "recharts";
import type { EntryViewT } from "../../../types/database/entries";
import {
	convertEntriesToChartConfig,
	convertEntriesToChartData,
} from "./utils";

export function PainChart({
	entries,
	formId,
	disableAnimations = false,
}: {
	entries: EntryViewT[];
	formId: string;
	disableAnimations?: boolean;
}) {
	const chartConfig = convertEntriesToChartConfig(entries, formId);
	const chartData = convertEntriesToChartData(entries, formId);

	return (
		<ChartContainer config={chartConfig}>
			<ResponsiveContainer width="100% max-w-[750px]" height={400}>
				<LineChart
					accessibilityLayer
					data={chartData}
					margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
				>
					<CartesianGrid vertical={false} />
					<XAxis
						dataKey="date"
						tickLine={false}
						tickMargin={10}
						axisLine={false}
						tickFormatter={(value) => {
							return dayjs(value).format("DD MMM");
						}}
						// Ensure text is visible in print
						className="print:!text-black"
					/>
					<YAxis
						domain={[0, 10]}
						tickLine={false}
						axisLine={false}
						tickMargin={10}
						label={{
							value: "Pain Score",
							angle: -90,
							position: "insideLeft",
						}}
						// Ensure text is visible in print
						className="print:!text-black"
					/>
					<ChartTooltip
						content={
							<ChartTooltipContent
								hideLabel
								className="p-3 w-[200px] md:w-[300px]"
								formatter={(value, name, item, index) => (
									<div>
										{index === 0 && (
											<div className="mb-2 pb-2 border-b">
												<div className="font-medium text-slate-900 text-sm capitalize">
													{item.payload.timing}
												</div>
												<div className="mt-1 text-slate-600 text-xs">
													{dayjs(item.payload.date).format("DD MMM YYYY")}
												</div>
											</div>
										)}
										<div className="flex justify-between items-center text-slate-800">
											<span className="font-medium text-sm capitalize">
												{Object.keys(chartConfig)[index]}
											</span>
											<div
												className={cn(
													"ml-2 px-2 py-0.5 rounded-sm font-mono font-medium tabular-nums text-foreground",
													{
														"bg-[hsl(var(--chart-1))]/50": index === 0,
														"bg-[hsl(var(--chart-2))]/50": index === 1,
														"bg-[hsl(var(--chart-3))]/50": index === 2,
													},
												)}
											>
												{value}/10
											</div>
										</div>
									</div>
								)}
							/>
						}
						cursor={false}
					/>
					<Line
						type="natural"
						dataKey={Object.keys(chartConfig)[0]}
						strokeWidth={2}
						stroke="hsl(var(--chart-1))"
						isAnimationActive={!disableAnimations}
						// Ensure lines are visible in print
						className="print:!stroke-[2px] print:!stroke-[hsl(var(--chart-1))]"
					/>
					{Object.keys(chartConfig).length > 1 && (
						<Line
							type="natural"
							dataKey={Object.keys(chartConfig)[1]}
							strokeWidth={2}
							stroke="hsl(var(--chart-2))"
							isAnimationActive={!disableAnimations}
							// Ensure lines are visible in print
							className="print:!stroke-[2px] print:!stroke-[hsl(var(--chart-2))]"
						/>
					)}
					{Object.keys(chartConfig).length > 2 && (
						<Line
							type="natural"
							dataKey={Object.keys(chartConfig)[2]}
							strokeWidth={2}
							stroke="hsl(var(--chart-3))"
							isAnimationActive={!disableAnimations}
							// Ensure lines are visible in print
							className="print:!stroke-[2px] print:!stroke-[hsl(var(--chart-3))]"
						/>
					)}
				</LineChart>
			</ResponsiveContainer>
		</ChartContainer>
	);
}
